/** Controlled boundary for systems that are not integrated with Academy yet. */
export type ExternalHandoffMode = "mock" | "disabled" | "live";

export interface ExternalHandoffRecord {
  mode: ExternalHandoffMode;
  destination: string;
  requestedAt: string;
  opened: boolean;
}

const STORAGE_KEY = "chatoner-academy:last-external-handoff";

export function externalHandoffMode(): ExternalHandoffMode {
  const configured = import.meta.env.VITE_EXTERNAL_HANDOFF_MODE;
  return configured === "live" || configured === "disabled" ? configured : "mock";
}

function showHandoffNotice(target: URL, mode: Exclude<ExternalHandoffMode, "live">) {
  const existing = document.getElementById("academy-external-handoff-notice");
  existing?.remove();

  const notice = document.createElement("div");
  notice.id = "academy-external-handoff-notice";
  notice.setAttribute("role", "status");
  notice.setAttribute("aria-live", "polite");
  notice.textContent =
    mode === "disabled"
      ? `${target.hostname} handoff is unavailable: this external integration is not certified for the private beta. No data was sent.`
      : `Demonstration handoff only: ${target.hostname} is not connected. No data was sent.`;
  Object.assign(notice.style, {
    position: "fixed",
    zIndex: "2147483647",
    left: "50%",
    bottom: "24px",
    transform: "translateX(-50%)",
    width: "min(560px, calc(100vw - 32px))",
    padding: "12px 16px",
    border: "1px solid rgba(47, 214, 244, .34)",
    borderRadius: "12px",
    background: "#10162e",
    color: "#f7f8ff",
    boxShadow: "0 18px 50px rgba(0, 0, 0, .45)",
    font: "600 14px/1.45 Inter, ui-sans-serif, system-ui, sans-serif",
    textAlign: "center",
  });
  document.body.appendChild(notice);
  window.setTimeout(() => notice.remove(), 5200);
}

export function requestExternalHandoff(url: string): ExternalHandoffRecord {
  const target = new URL(url, window.location.origin);
  const mode = externalHandoffMode();
  const record: ExternalHandoffRecord = {
    mode,
    destination: target.toString(),
    requestedAt: new Date().toISOString(),
    opened: false,
  };

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Restricted browser storage must not prevent the mock interaction.
  }
  window.dispatchEvent(new CustomEvent("academy:external-handoff", { detail: record }));

  if (mode !== "live") {
    showHandoffNotice(target, mode);
    return record;
  }
  if (target.protocol !== "https:") throw new Error("Live external handoffs require HTTPS.");

  const opened = window.open(target.toString(), "_blank", "noopener,noreferrer");
  if (opened) opened.opener = null;
  else window.location.assign(target.toString());
  return { ...record, opened: true };
}
